/**
 * Do Not Contact admin page
 * Lists all DNC entries and allows adding/removing them.
 *
 * Removal is the destructive direction here: taking an entry off this list re-enables
 * outreach to somebody who asked not to be contacted. So both the single and the bulk
 * remove confirm first (the single one did not, before), and there is deliberately no
 * "remove everything matching this search" — the API has no filter mode for the same
 * reason. See `DoNotContactBulkDeleteRequest` in crm.py.
 */
import { useState, useEffect, useMemo } from 'react'
import { Navigate } from 'react-router-dom'
import { format } from 'date-fns'
import { parseServerDate } from '../../utils/formatDateTime'
import { useAuth } from '../../services/auth'
import { listDnc, createDnc, deleteDnc, bulkDeleteDnc, updateDnc
} from '../../services/crm'
import type { DoNotContactEntry, DncMatchType, DncSortKey } from '../../types'
import { Pagination } from '../../components/Pagination'
import FilterBar from '../../components/FilterBar'
import PageHeading from '../../components/PageHeading'
import { DNC_HELP } from '../../content/pageHelp'
import Modal from '../../components/Modal'
import { SortableHeader } from '../../components/SortableHeader'
import { useTableSort } from '../../hooks/useTableSort'
import { RowCheckbox, SelectAllCheckbox, BulkActionBar } from '../../components/BulkSelect'
import { useRowSelection } from '../../hooks/useRowSelection'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import {
  isCode, isPageNumber, listStateKey, textUpTo, usePageSize, useSessionState,
} from '../../hooks/useSessionState'

// Filters, page and sort are remembered for the tab (useSessionState).
const KEY = listStateKey('doNotContact')
const DNC_SORT_KEYS: readonly DncSortKey[] = ['created_at', 'value', 'match_type']

const MATCH_TYPE_LABELS: Record<string, string> = {
  call_sign: 'Call Sign',
  entity_name: 'Entity Name',
  fcc_usi: 'FCC USI',
  email: 'Email Address',
}

const MATCH_TYPE_OPTIONS = [
  { value: '', label: 'Any match type' },
  { value: 'call_sign', label: 'Call Sign' },
  { value: 'entity_name', label: 'Entity Name' },
  { value: 'fcc_usi', label: 'FCC USI' },
  { value: 'email', label: 'Email Address' },
]

/** Placeholder per match type — an entry only works if it is the right shape. */
const VALUE_PLACEHOLDERS: Record<string, string> = {
  call_sign: 'e.g. WPXR456',
  fcc_usi: 'e.g. 123456789',
  email: 'e.g. someone@example.com',
  entity_name: 'Entity name',
}

export default function DoNotContactPage() {
  const { user } = useAuth()

  const [entries, setEntries] = useState<DoNotContactEntry[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useSessionState(KEY('page'), 1, isPageNumber)
  const [pageSize, setPageSize] = usePageSize('doNotContact', 25)
  const totalPages = Math.ceil(total / pageSize)

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  // Add form state
  const [showAddForm, setShowAddForm] = useState(false)
  const [addMatchType, setAddMatchType] = useState('call_sign')
  const [addValue, setAddValue] = useState('')
  const [addReason, setAddReason] = useState('')
  const [addLoading, setAddLoading] = useState(false)
  const [addError, setAddError] = useState('')

  // Remove state. `confirming` holds what is about to be removed: one entry, or the
  // current selection.
  const [removingId, setRemovingId] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState<null | { entry: DoNotContactEntry } | { bulk: true }>(null)

  // Filters
  const [search, setSearch] = useSessionState(KEY('search'), '', textUpTo(200))
  const debouncedSearch = useDebouncedValue(search)
  const [matchType, setMatchType] = useSessionState(KEY('matchType'), '', isCode)
  const { sortKey, sortOrder, handleSort } = useTableSort<DncSortKey>(
    'created_at', 'desc',
    {
      descFirst: ['created_at'], onSortChange: () => setPage(1),
      persist: { key: KEY('sort'), keys: DNC_SORT_KEYS },
    },
  )
  /** Show lifted entries too. Off by default — the working list is the live one —
      but the history has to be reachable, because "who took this person off the
      list" is the question a suppression list most needs to answer. */
  const [includeRemoved, setIncludeRemoved] = useSessionState(KEY('includeRemoved'), false)
  /** Inline reason editing. Entries flagged from a search result carry no reason,
      because the toggle does not collect one; this is how they get one afterwards. */
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editReason, setEditReason] = useState('')
  const [editError, setEditError] = useState('')

  const isAdmin = Boolean(user?.is_superuser)
  /**
   * Employees may read the list and record WHY somebody is on it (2026-09-21).
   *
   * Everything that REMOVES a suppression — delete, bulk delete — stays admin-only,
   * enforced by the API and hidden here so nobody is offered a button that 403s.
   * Creating stays admin-only on this page too; the search-results toggle is the
   * employee path for adding, and it collects a reason.
   */
  const isEmployee = isAdmin || (user?.roles ?? []).includes('employee')

  const selection = useRowSelection<number>(
    entries.map((e) => e.id),
    JSON.stringify([debouncedSearch, matchType, sortKey, sortOrder, page]),
  )

  /** Any filter change re-reads page 1 — page 3 of the old list is not page 3 of the new one. */
  const applyFilter = useMemo(
    () =>
      <T,>(set: (v: T) => void) =>
        (value: T) => {
          set(value)
          setPage(1)
        },
    [setPage],
  )

  // The redirect below must come AFTER every hook — returning early above this
  // effect made the hook conditional, so React would see a different hook order
  // for an admin and a non-admin. Today the guard happens to run on the first
  // render for both, but any change that let `user` arrive asynchronously would
  // have turned that into a crash. Same shape as the other admin pages
  // (AuditLogPage, BackupPage, …): hooks first, redirect last.
  useEffect(() => {
    if (!isEmployee) return
    load()
  }, [page, pageSize, isEmployee, debouncedSearch, matchType, sortKey, sortOrder, includeRemoved]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!isEmployee) {
    return <Navigate to="/" replace />
  }

  /**
   * Save an edited reason.
   *
   * Only ever sends `reason`. Changing `value` or `match_type` is admin-only server
   * side — it silently un-suppresses whoever the entry used to match — and this page
   * deliberately offers no control for it: the honest way to change what an entry
   * matches is to lift it and add the right one, so the removal is recorded.
   */
  async function saveReason(entry: DoNotContactEntry) {
    setEditError('')
    try {
      const updated = await updateDnc(entry.id, { reason: editReason.trim() })
      setEntries((prev) => prev.map((e) => (e.id === entry.id ? updated : e)))
      setEditingId(null)
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to save')
    }
  }

  async function load() {
    setLoading(true)
    setLoadError('')
    try {
      const resp = await listDnc({
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(matchType ? { match_type: matchType as DncMatchType } : {}),
        sort: sortKey,
        order: sortOrder,
        ...(includeRemoved ? { include_removed: true } : {}),
        page,
        page_size: pageSize,
      })
      setEntries(resp.items)
      setTotal(resp.total)
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load DNC list')
    } finally {
      setLoading(false)
    }
  }

  async function handleBulkRemove() {
    setBusy(true)
    setLoadError('')
    try {
      await bulkDeleteDnc([...selection.selected])
      selection.clear()
      await load()
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to remove entries')
    } finally {
      setBusy(false)
      setConfirming(null)
    }
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    if (!addValue.trim()) {
      setAddError('Value is required')
      return
    }
    setAddLoading(true)
    setAddError('')
    try {
      await createDnc({
        match_type: addMatchType,
        value: addValue.trim(),
        reason: addReason.trim() || undefined,
      })
      setShowAddForm(false)
      setAddValue('')
      setAddReason('')
      setAddMatchType('call_sign')
      // Reload the list
      await load()
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add entry')
    } finally {
      setAddLoading(false)
    }
  }

  async function handleRemove(id: number) {
    setRemovingId(id)
    try {
      await deleteDnc(id)
      setEntries((prev) => prev.filter((e) => e.id !== id))
      setTotal((prev) => Math.max(0, prev - 1))
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to remove entry')
    } finally {
      setRemovingId(null)
      setConfirming(null)
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <PageHeading
          title="Do Not Contact" help={DNC_HELP}
          subtitle="Licenses on this list are flagged in search results and suppressed from lead generation."
        />
        {isAdmin && (
        <button
          type="button"
          onClick={() => { setShowAddForm(!showAddForm); setAddError('') }}
          className="btn btn-primary"
        >
          {showAddForm ? 'Cancel' : 'Add Entry'}
        </button>
      )}
      </div>

      {/* Add form */}
      {isAdmin && showAddForm && (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Add DNC Entry</h2>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="dnc-match-type" className="label">Match Type</label>
                <select
                  id="dnc-match-type"
                  className="input"
                  value={addMatchType}
                  onChange={(e) => setAddMatchType(e.target.value)}
                >
                  <option value="call_sign">Call Sign</option>
                  <option value="entity_name">Entity Name</option>
                  <option value="fcc_usi">FCC USI</option>
                  <option value="email">Email Address</option>
                </select>
                {addMatchType === 'email' && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Stops email to this address. The other three match a license, so
                    they also cover renewal reminders; an address only stops mail
                    sent to it.
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="dnc-value" className="label">Value</label>
                <input
                  id="dnc-value"
                  type="text"
                  className="input"
                  placeholder={VALUE_PLACEHOLDERS[addMatchType] ?? 'Value'}
                  maxLength={255}
                  value={addValue}
                  onChange={(e) => setAddValue(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="dnc-reason" className="label">Reason (optional)</label>
                <input
                  id="dnc-reason"
                  type="text"
                  className="input"
                  placeholder="Why is this contact do-not-contact?"
                  maxLength={500}
                  value={addReason}
                  onChange={(e) => setAddReason(e.target.value)}
                />
              </div>
            </div>
            {addError && <p className="text-sm text-red-600" role="alert">{addError}</p>}
            <div className="flex gap-2">
              <button type="submit" className="btn btn-primary" disabled={addLoading}>
                {addLoading ? 'Adding…' : 'Add Entry'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setShowAddForm(false); setAddError('') }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <FilterBar
        searchValue={search}
        onSearchChange={applyFilter(setSearch)}
        searchPlaceholder="Search value or reason…"
        searchLabel="Search the Do Not Contact list"
        selects={[
          {
            id: 'dnc-match-type-filter',
            label: 'Filter by match type',
            value: matchType,
            onChange: applyFilter(setMatchType),
            options: MATCH_TYPE_OPTIONS,
          },
        ]}
      />

      <label className="mb-4 flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={includeRemoved}
          onChange={(e) => { setIncludeRemoved(e.target.checked); setPage(1) }}
        />
        {/* Removals used to vanish from this page entirely, so the event most worth
            keeping on a suppression list was the one nobody could look up. */}
        Show entries that have been removed
      </label>

      {loadError && <div className="alert alert-error mb-4">{loadError}</div>}

      {isAdmin && <BulkActionBar
        count={selection.count}
        total={total}
        pageSize={pageSize}
        // No filter mode on this endpoint, so there is nothing to escalate to:
        // removing suppressions is not something to do to rows you have not seen.
        allMatching={false}
        onSelectAllMatching={() => {}}
        onClear={selection.clear}
      >
        <button
          type="button"
          onClick={() => setConfirming({ bulk: true })}
          disabled={busy}
          className="btn-sm btn-danger disabled:opacity-50"
        >
          {busy ? 'Removing…' : 'Remove selected'}
        </button>
      </BulkActionBar>}

      <div className="card">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {search || matchType
                ? 'No entries match these filters.'
                : 'No Do Not Contact entries yet.'}
            </p>
            {!search && !matchType && (
              <p className="text-sm text-gray-400 mt-1">
                Add entries to suppress lead generation and flag licenses in search results.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="mb-3 text-sm text-gray-500">{total} total {total === 1 ? 'entry' : 'entries'}</div>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    {isAdmin && (
                      <th className="w-px">
                        <SelectAllCheckbox state={selection.pageState} onToggle={selection.togglePage} />
                      </th>
                    )}
                    <SortableHeader
                      sortKey="match_type"
                      currentSortKey={sortKey}
                      currentSortOrder={sortOrder}
                      onSort={handleSort}
                    >
                      Match Type
                    </SortableHeader>
                    <SortableHeader
                      sortKey="value"
                      currentSortKey={sortKey}
                      currentSortOrder={sortOrder}
                      onSort={handleSort}
                    >
                      Value
                    </SortableHeader>
                    <th>Reason</th>
                    <SortableHeader
                      sortKey="created_at"
                      currentSortKey={sortKey}
                      currentSortOrder={sortOrder}
                      onSort={handleSort}
                    >
                      Added At
                    </SortableHeader>
                    {includeRemoved && <th>Removed</th>}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {entries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      {isAdmin && (
                        <td className="w-px">
                          <RowCheckbox
                            checked={selection.isSelected(entry.id)}
                            onChange={() => selection.toggle(entry.id)}
                            label={entry.value}
                          />
                        </td>
                      )}
                      <td>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {MATCH_TYPE_LABELS[entry.match_type] ?? entry.match_type}
                        </span>
                      </td>
                      <td className="font-medium text-gray-900">{entry.value}</td>
                      <td className="text-gray-600 max-w-xs">
                        {editingId === entry.id ? (
                          <div className="flex items-center gap-1">
                            <input
                              className="input py-1 text-sm"
                              value={editReason}
                              maxLength={2000}
                              autoFocus
                              onChange={(e) => setEditReason(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') void saveReason(entry)
                                if (e.key === 'Escape') setEditingId(null)
                              }}
                            />
                            <button
                              type="button"
                              className="tap text-sm text-primary-600"
                              onClick={() => void saveReason(entry)}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="tap text-sm text-gray-500"
                              onClick={() => { setEditingId(null); setEditError('') }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="text-left w-full truncate hover:text-primary-700"
                            title={entry.reason ?? 'Click to add a reason'}
                            onClick={() => {
                              setEditingId(entry.id)
                              setEditReason(entry.reason ?? '')
                              setEditError('')
                            }}
                          >
                            {entry.reason || (
                              <span className="text-gray-400 italic">add a reason</span>
                            )}
                          </button>
                        )}
                        {editingId === entry.id && editError && (
                          <p className="text-xs text-red-600 mt-1" role="alert">{editError}</p>
                        )}
                      </td>
                      <td className="text-gray-500 text-sm whitespace-nowrap">
                        {format(parseServerDate(entry.created_at)!, 'MM/dd/yyyy')}
                      </td>
                      {includeRemoved && (
                        <td className="text-sm whitespace-nowrap">
                          {entry.deleted_at ? (
                            <span
                              className="text-gray-500"
                              title={entry.removal_reason || 'No reason recorded'}
                            >
                              {format(parseServerDate(entry.deleted_at)!, 'MM/dd/yyyy')}
                              {entry.removal_reason && (
                                <span className="ml-1 italic">— {entry.removal_reason}</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-green-700 text-xs font-medium">active</span>
                          )}
                        </td>
                      )}
                      <td>
                        {/* An employee may lift only what they added; an admin may lift
                            anything. Hidden rather than shown-and-403'd, and an entry
                            with no recorded creator is admin-only — "nobody recorded who
                            added this" must not read as "anyone may remove it". */}
                        {(isAdmin
                          || (entry.created_by_user_id != null
                              && entry.created_by_user_id === user?.id)) && <button
                          type="button"
                          onClick={() => setConfirming({ entry })}
                          disabled={removingId === entry.id}
                          className="tap text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                          title="Remove this DNC entry"
                        >
                          {removingId === entry.id ? 'Removing…' : 'Remove'}
                        </button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page} totalPages={totalPages} onPageChange={setPage}
              total={total} pageSize={pageSize}
              onPageSizeChange={(n) => { setPageSize(n); setPage(1) }}
            />
          </>
        )}
      </div>

      {confirming && (
        <Modal
          titleId="dnc-remove-confirm"
          title={
            'bulk' in confirming
              ? `Remove ${selection.count} entr${selection.count === 1 ? 'y' : 'ies'}?`
              : 'Remove this entry?'
          }
          onClose={() => setConfirming(null)}
          maxWidth="max-w-md"
        >
          <p className="text-sm text-gray-600">
            {/* Say what removal *does*, not just that it is permanent — the
                consequence is outreach resuming, and that is the part worth pausing
                over. */}
            {'bulk' in confirming
              ? `These ${selection.count} will no longer be suppressed: they can be contacted again and will reappear in lead generation.`
              : `“${confirming.entry.value}” will no longer be suppressed: it can be contacted again and will reappear in lead generation.`}
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setConfirming(null)} className="btn btn-secondary">
              Cancel
            </button>
            <button
              type="button"
              onClick={() =>
                'bulk' in confirming ? handleBulkRemove() : handleRemove(confirming.entry.id)
              }
              disabled={busy || removingId !== null}
              className="btn btn-danger disabled:opacity-50"
            >
              {busy || removingId !== null ? 'Removing…' : 'Remove'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}
